// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the pure parts of the Scheduled page (design/scheduled, direction A): filters and their
// counts, day groups and where the now line goes, repeat chips, snooze presets, readable automation
// steps and run lines. No strings here: the components turn these shapes into Lingui messages.

import type {
	ScheduledAlwaysOn,
	ScheduledDigest,
	ScheduledItem,
	ScheduledKind,
	ScheduledSource,
} from '@app/features/konvo_scheduled/state/KonvoScheduled';
import type {
	AssistantFlowDefinition,
	AssistantFlowRunView,
	FlowRepeat,
	FlowStep,
	FlowTrigger,
} from '@konvo/schema/src/domains/konvo/AssistantFlow';

/** `waiting`: questions waiting on a reply, both ways (konvo_waiting); not Scheduled items. */
export type ScheduledFilter =
	| 'all'
	| 'waiting'
	| 'reminders'
	| 'sends'
	| 'automations'
	| 'digests'
	| 'promises'
	| 'snoozed';

export const SCHEDULED_FILTERS: ReadonlyArray<ScheduledFilter> = [
	'all',
	'waiting',
	'reminders',
	'sends',
	'automations',
	'digests',
	'promises',
	'snoozed',
];

export const FILTER_KIND: Record<Exclude<ScheduledFilter, 'all' | 'waiting'>, ScheduledKind> = {
	reminders: 'reminder',
	sends: 'message',
	automations: 'flow',
	digests: 'digest',
	promises: 'promise',
	snoozed: 'snooze',
};

/** `?filter=` from the URL; anything unknown is All. */
export function parseFilter(value: string | null | undefined): ScheduledFilter {
	return SCHEDULED_FILTERS.includes(value as ScheduledFilter) ? (value as ScheduledFilter) : 'all';
}

export function itemMatchesFilter(item: Pick<ScheduledItem, 'kind'>, filter: ScheduledFilter): boolean {
	if (filter === 'waiting') return false;
	return filter === 'all' || FILTER_KIND[filter] === item.kind;
}

/** Search over what the row shows: title, second line and who or where. */
export function matchesQuery(item: Pick<ScheduledItem, 'title' | 'detail' | 'label'>, query: string): boolean {
	const needle = query.trim().toLowerCase();
	if (!needle) return true;
	return [item.title, item.detail, item.label].some((text) => text?.toLowerCase().includes(needle));
}

/**
 * Chip counts are things, not occurrences: a weekday automation counts once. Automations include
 * the always-on ones and drafts waiting for review; Digests count every digest, switched on or
 * off, since the filter lists them all.
 */
export function filterCounts(data: {
	items: ReadonlyArray<Pick<ScheduledItem, 'kind' | 'id'>>;
	alwaysOn: ReadonlyArray<Pick<ScheduledAlwaysOn, 'id'>>;
	digests: ReadonlyArray<Pick<ScheduledDigest, 'id'>>;
	drafts?: ReadonlyArray<{id: string}>;
}): Record<ScheduledFilter, number> {
	const ids: Record<ScheduledKind, Set<string>> = {
		reminder: new Set(),
		message: new Set(),
		flow: new Set(),
		digest: new Set(),
		promise: new Set(),
		snooze: new Set(),
	};
	for (const item of data.items) ids[item.kind]?.add(item.id);
	for (const flow of data.alwaysOn) ids.flow.add(flow.id);
	for (const draft of data.drafts ?? []) ids.flow.add(draft.id);
	for (const digest of data.digests) ids.digest.add(digest.id);
	const counts = {
		all: 0,
		// Filled in by the page from the waiting list (konvo_waiting), which is not a Scheduled item.
		waiting: 0,
		reminders: ids.reminder.size,
		sends: ids.message.size,
		automations: ids.flow.size,
		digests: ids.digest.size,
		promises: ids.promise.size,
		snoozed: ids.snooze.size,
	};
	counts.all = counts.reminders + counts.sends + counts.automations + counts.digests + counts.promises + counts.snoozed;
	return counts;
}

/** "13 coming up": things with an occurrence still ahead, each counted once. */
export function comingUpCount(items: ReadonlyArray<Pick<ScheduledItem, 'kind' | 'id' | 'at'>>, now: number): number {
	const ahead = new Set<string>();
	for (const item of items) if (Date.parse(item.at) >= now) ahead.add(`${item.kind}:${item.id}`);
	return ahead.size;
}

export function isPast(item: Pick<ScheduledItem, 'at'>, now: number): boolean {
	return Date.parse(item.at) < now;
}

/** Local calendar day, "2026-10-14". */
export function dayKey(date: Date): string {
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${date.getFullYear()}-${month}-${day}`;
}

export interface DayGroup<T extends Pick<ScheduledItem, 'at'> = ScheduledItem> {
	key: string;
	/** Local midnight of the day. */
	date: Date;
	items: Array<T>;
}

/** Items (already sorted by time) into local days, in order. */
export function groupByDay<T extends Pick<ScheduledItem, 'at'>>(items: ReadonlyArray<T>): Array<DayGroup<T>> {
	const groups: Array<DayGroup<T>> = [];
	for (const item of items) {
		const at = new Date(item.at);
		const key = dayKey(at);
		let group = groups[groups.length - 1];
		if (!group || group.key !== key) {
			group = {key, date: new Date(at.getFullYear(), at.getMonth(), at.getDate()), items: []};
			groups.push(group);
		}
		group.items.push(item);
	}
	return groups;
}

/** How a day header reads: Today, Tomorrow, a weekday name this week, or a date further on. */
export type DayName = 'today' | 'tomorrow' | 'weekday' | 'date';

export function dayName(date: Date, now: Date): DayName {
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
	const days = Math.round((target.getTime() - today.getTime()) / 86_400_000);
	if (days === 0) return 'today';
	if (days === 1) return 'tomorrow';
	if (days > 1 && days < 7) return 'weekday';
	return 'date';
}

/** Where the now line sits among a day's items: before the first one still to come. */
export function nowLineIndex(items: ReadonlyArray<Pick<ScheduledItem, 'at'>>, now: number): number {
	const index = items.findIndex((item) => Date.parse(item.at) >= now);
	return index === -1 ? items.length : index;
}

/** The repeat chip: Daily, Weekdays, a weekday ("Fridays"), Monthly, or a rule ("Last Monday of every month"). */
export type RepeatLabel =
	| {kind: 'daily'}
	| {kind: 'weekdays'}
	| {kind: 'weekly'; weekday: number}
	| {kind: 'monthly'}
	| {kind: 'days'; weekdays: Array<number>}
	| {kind: 'rule'; rule: FlowRepeat; at: string};

/** ISO weekday (1 = Monday) of a date in local time. */
export function isoWeekday(date: Date): number {
	return ((date.getDay() + 6) % 7) + 1;
}

export function repeatOf(
	item: Pick<ScheduledItem, 'recurrence' | 'weekdays' | 'at'> & {repeat?: FlowRepeat | null},
): RepeatLabel | null {
	if (item.repeat) return {kind: 'rule', rule: item.repeat, at: item.at};
	const days = item.weekdays ? [...new Set(item.weekdays)].sort((a, b) => a - b) : null;
	if (days && days.length === 7) return {kind: 'daily'};
	if (days && days.length === 5 && days.join() === '1,2,3,4,5') return {kind: 'weekdays'};
	if (days && days.length === 1) return {kind: 'weekly', weekday: days[0]};
	if (days && days.length > 1) return {kind: 'days', weekdays: days};
	switch (item.recurrence) {
		case 'daily':
			return {kind: 'daily'};
		case 'weekdays':
			return {kind: 'weekdays'};
		case 'weekly':
			return {kind: 'weekly', weekday: isoWeekday(new Date(item.at))};
		case 'monthly':
			return {kind: 'monthly'};
		default:
			return null;
	}
}

/** A schedule trigger as a repeat and a time ("Weekdays at 08:00"); null for event triggers. */
export function scheduleOf(trigger: FlowTrigger): {repeat: RepeatLabel | null; at: Date} | null {
	if (trigger.kind !== 'schedule') return null;
	const at = new Date(trigger.at);
	if (Number.isNaN(at.getTime())) return null;
	return {
		repeat: repeatOf({recurrence: trigger.recurrence, repeat: trigger.repeat, weekdays: null, at: trigger.at}),
		at,
	};
}

export type SnoozeKey = 'hour' | 'evening' | 'tomorrow';

export interface SnoozePreset {
	key: SnoozeKey;
	at: Date;
}

/** In an hour, this evening at 18:00 (until 17:00), tomorrow at 09:00. */
export function snoozePresets(now: Date): Array<SnoozePreset> {
	const hour = new Date(now.getTime() + 3_600_000);
	hour.setSeconds(0, 0);
	const out: Array<SnoozePreset> = [{key: 'hour', at: hour}];
	const evening = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 18, 0);
	if (evening.getTime() - now.getTime() >= 3_600_000) out.push({key: 'evening', at: evening});
	out.push({key: 'tomorrow', at: new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 9, 0)});
	return out;
}

/** A digest's local "HH:MM" slot as a date today, for formatting like the other times. */
export function slotDate(at: string, now: Date): Date {
	const [hour, minute] = at.split(':').map(Number);
	return new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour || 0, minute || 0);
}

/** `<input type="datetime-local">` value for a date, local time. */
export function localInputValue(date: Date): string {
	const pad = (value: number) => String(value).padStart(2, '0');
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** What one automation step does, for the Does list. `title` wins when the assistant wrote one. */
export type StepVerb =
	| 'read'
	| 'summarize'
	| 'send'
	| 'mail'
	| 'task'
	| 'wait'
	| 'approval'
	| 'check'
	| 'each'
	| 'agentStart'
	| 'agentPrompt'
	| 'agentsDigest'
	| 'other';

export interface StepLine {
	id: string;
	title: string | null;
	verb: StepVerb;
	/** Destination label ("#dev", "Berno Myburg") for sends. */
	target: string | null;
	source: ScheduledSource | null;
}

function stepSource(step: FlowStep): ScheduledSource | null {
	const name = `${step.action ?? ''} ${step.tool ?? ''}`;
	const destination = typeof step.args?.['destination_id'] === 'string' ? (step.args['destination_id'] as string) : '';
	if (destination.startsWith('whatsapp:')) return 'whatsapp';
	if (/mail/.test(name)) return 'outlook';
	if (/task/.test(name)) return 'clickup';
	if (/github|pull/.test(name)) return 'github';
	// The agents digest goes to the person's Konvo DM.
	if (step.action === 'send_message' || step.action === 'send_agents_digest') return 'konvo';
	return null;
}

function stepVerb(step: FlowStep): StepVerb {
	const name = step.action ?? step.tool ?? '';
	switch (step.kind) {
		case 'read':
			return 'read';
		case 'summarize':
			return 'summarize';
		case 'wait':
			return 'wait';
		case 'approval':
			return 'approval';
		case 'branch':
			return 'check';
		case 'for_each':
			return 'each';
		default:
			if (name === 'start_agent_thread') return 'agentStart';
			if (name === 'prompt_agent_thread') return 'agentPrompt';
			if (name === 'send_agents_digest') return 'agentsDigest';
			if (name === 'send_message') return 'send';
			if (/mail/.test(name)) return 'mail';
			if (/task/.test(name)) return 'task';
			return 'other';
	}
}

/** The review label key of an agent step's machine and project or thread (FlowService.flowDestinations). */
export function agentPlaceKey(step: Pick<FlowStep, 'action' | 'args'>): string | null {
	if (step.action !== 'start_agent_thread' && step.action !== 'prompt_agent_thread') return null;
	const args = step.args ?? {};
	const place = step.action === 'start_agent_thread' ? args['project_id'] : args['thread_id'];
	return typeof args['machine_id'] === 'string' && typeof place === 'string'
		? `agent:${args['machine_id']}:${place}`
		: null;
}

/** The top-level steps of an automation, readable. */
export function flowSteps(
	definition: AssistantFlowDefinition,
	destinations: Record<string, string> = {},
): Array<StepLine> {
	return definition.steps.map((step) => {
		const destination =
			typeof step.args?.['destination_id'] === 'string' ? (step.args['destination_id'] as string) : null;
		const agentPlace = agentPlaceKey(step);
		return {
			id: step.id,
			title: step.title?.trim() || null,
			verb: stepVerb(step),
			target: destination
				? (destinations[destination] ?? null)
				: agentPlace
					? (destinations[agentPlace] ?? null)
					: null,
			source: stepSource(step),
		};
	});
}

/** The brand an automation lands in, from its first step that has one (for the tile badge). */
export function flowSource(definition: AssistantFlowDefinition): ScheduledSource | null {
	return flowSteps(definition).find((step) => step.source)?.source ?? null;
}

export type RunTone = 'ok' | 'skipped' | 'failed' | 'busy';

export interface RunLine {
	id: string;
	at: Date;
	tone: RunTone;
	/** What it did ("Posted 3 tasks in #dev") or why it stopped; null shows the status word. */
	text: string | null;
}

const SHORT_RESULT = 80;

export function runLine(run: AssistantFlowRunView): RunLine {
	const tone: RunTone =
		run.status === 'succeeded'
			? 'ok'
			: run.status === 'failed' || run.status === 'unknown'
				? 'failed'
				: run.status === 'cancelled'
					? 'skipped'
					: 'busy';
	const last = run.receipts[run.receipts.length - 1];
	const preview = last && !last.truncated && !/^[[{]/.test(last.preview) ? last.preview.trim() : '';
	const text = run.reason?.trim() || (preview && preview.length <= SHORT_RESULT ? preview : null);
	return {id: run.id, at: new Date(run.created_at), tone, text};
}

/** Newest first, at most `limit`. */
export function lastRuns(runs: ReadonlyArray<AssistantFlowRunView>, limit = 3): Array<RunLine> {
	return [...runs]
		.sort((a, b) => b.created_at.localeCompare(a.created_at))
		.slice(0, limit)
		.map(runLine);
}

/** How long a snooze change made here is laid over the server's answer while it catches up. */
const SNOOZE_EDIT_MS = 120_000;

export interface SnoozeEdit {
	/** When it comes back now; null when it was brought back. */
	until: number | null;
	madeAt: number;
}

/**
 * Snoozes changed on this page, laid over a server answer that may not have them yet: one
 * brought back is left out, one snoozed again moves to its new time. An edit the server
 * already agrees with is dropped.
 */
export function applySnoozeEdits(
	items: ReadonlyArray<ScheduledItem>,
	edits: Map<string, SnoozeEdit>,
	nowMs: number,
): Array<ScheduledItem> {
	for (const [id, edit] of edits) if (nowMs - edit.madeAt > SNOOZE_EDIT_MS) edits.delete(id);
	if (edits.size === 0) return [...items];
	const out: Array<ScheduledItem> = [];
	const seen = new Set<string>();
	for (const item of items) {
		const edit = item.kind === 'snooze' ? edits.get(item.id) : undefined;
		if (!edit) {
			out.push(item);
			continue;
		}
		seen.add(item.id);
		if (edit.until == null) continue;
		if (Date.parse(item.at) === edit.until) edits.delete(item.id);
		out.push({...item, at: new Date(edit.until).toISOString()});
	}
	for (const [id, edit] of edits) if (edit.until == null && !seen.has(id)) edits.delete(id);
	return out.sort((a, b) => a.at.localeCompare(b.at) || a.title.localeCompare(b.title));
}
